import mainApiClient from "../helpers/services/main-api-client";

/**
 * Canva Connect (REST) — el intercambio de código OAuth y el client secret
 * deben ocurrir en el backend (Canva bloquea CORS en el navegador).
 *
 * Contrato esperado en REACT_APP_MAIN_URL (ej. http://localhost:5000/api):
 *
 * GET  canva/connection
 *      → { connected: boolean, oauthUrl?: string }
 *      Si !connected y hay oauthUrl, redirigir al usuario para autorizar.
 *
 * GET  canva/designs?limit=25&continuation=...
 *      → { items: CanvaDesignItem[], continuation?: string }
 *
 * POST canva/exports
 *      body: { designId: string, format?: 'jpg' | 'png', quality?: number }
 *      → { job: { id: string, status: string, urls?: string[], error?: { message?: string } } }
 *
 * GET  canva/exports/:jobId
 *      → mismo shape de job (polling hasta success | failed)
 */
export interface CanvaConnectionResponse {
  connected: boolean;
  /** URL completa a la que redirigir para iniciar OAuth con Canva */
  oauthUrl?: string;
}

export interface CanvaDesignItem {
  id: string;
  title: string;
  thumbnail?: {
    url: string;
    width: number;
    height: number;
  };
}

export interface CanvaDesignsResponse {
  items: CanvaDesignItem[];
  continuation?: string;
}

export interface CanvaExportJob {
  id: string;
  status: "in_progress" | "success" | "failed";
  urls?: string[];
  error?: { code?: string; message?: string };
}

export interface CanvaExportResponse {
  job: CanvaExportJob;
}

export async function fetchCanvaConnection(): Promise<CanvaConnectionResponse> {
  const api = mainApiClient();
  const { data } = await api.get<CanvaConnectionResponse>("canva/connection");
  return data;
}

export async function fetchCanvaDesigns(params?: {
  limit?: number;
  continuation?: string;
}): Promise<CanvaDesignsResponse> {
  const api = mainApiClient();
  const { data } = await api.get<CanvaDesignsResponse>("canva/designs", {
    params: {
      limit: params?.limit ?? 30,
      continuation: params?.continuation,
    },
  });
  return data;
}

export async function createCanvaExport(body: {
  designId: string;
  format?: "jpg" | "png";
  quality?: number;
}): Promise<CanvaExportResponse> {
  const api = mainApiClient();
  const { data } = await api.post<CanvaExportResponse>("canva/exports", {
    designId: body.designId,
    format: body.format ?? "jpg",
    quality: body.quality ?? 90,
  });
  return data;
}

export async function fetchCanvaExportJob(jobId: string): Promise<CanvaExportResponse> {
  const api = mainApiClient();
  const { data } = await api.get<CanvaExportResponse>(`canva/exports/${jobId}`);
  return data;
}

const POLL_MS = 600;
const POLL_MAX = 80;

export async function waitForCanvaExport(
  jobId: string,
  onProgress?: (status: string) => void
): Promise<string[]> {
  for (let i = 0; i < POLL_MAX; i += 1) {
    const { job } = await fetchCanvaExportJob(jobId);
    onProgress?.(job.status);
    if (job.status === "success" && job.urls?.length) {
      return job.urls;
    }
    if (job.status === "failed") {
      throw new Error(job.error?.message || "Canva export failed");
    }
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
  throw new Error("Canva export timed out");
}

/** Crea export y devuelve URLs; si el backend devuelve el job ya en success, no hace polling. */
export async function createCanvaExportAndGetUrls(body: {
  designId: string;
  format?: "jpg" | "png";
  quality?: number;
}): Promise<string[]> {
  const { job } = await createCanvaExport(body);
  if (job.status === "success" && job.urls?.length) {
    return job.urls;
  }
  if (job.status === "failed") {
    throw new Error(job.error?.message || "Canva export failed");
  }
  return waitForCanvaExport(job.id);
}
